import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { AlertTriangle, Plus, Pencil, Trash2, CheckCircle, Clock, Package, AlertCircle, Upload, FileText, X, Eye, Download, ShoppingCart, ChevronDown, ChevronRight, Layers, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DepotDefective = () => {
  const { user } = useAuth();
  const [urunler, setUrunler] = useState([]);
  const [stokKartlari, setStokKartlari] = useState([]);
  const [cariHesaplar, setCariHesaplar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('acik');
  const [expandedOrders, setExpandedOrders] = useState({});
  
  const [modal, setModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [selectedUrun, setSelectedUrun] = useState(null);
  
  // Hata Giderildi modal state
  const [resolveModal, setResolveModal] = useState(false);
  const [resolveUrun, setResolveUrun] = useState(null);
  const [resolveNotes, setResolveNotes] = useState('');
  
  const [form, setForm] = useState({
    id: '',
    stok_karti_id: '',
    product_name: '',
    issue_type: 'hatali',
    quantity: '',
    unit: 'adet',
    cari_hesap: '',
    hata_cikma_zamani: '',
    description: '',
    belgeler: [],
    status: 'acik',
    resolution_notes: ''
  });

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('fortis_token')}`,
    'Content-Type': 'application/json'
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [urunlerRes, stokRes, cariRes] = await Promise.all([
        axios.get(`${API}/api/depot/hatali-urunler?group_by_order=true`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/depot/stok-kartlari`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/depot/cari-hesaplar`, { headers: getAuthHeaders() })
      ]);
      setUrunler(urunlerRes.data);
      setStokKartlari(stokRes.data);
      setCariHesaplar(cariRes.data);
    } catch (err) {
      console.error('Veri yüklenemedi:', err);
      setError('Veriler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setForm({
      id: '',
      stok_karti_id: '',
      product_name: '',
      issue_type: 'hatali',
      quantity: '',
      unit: 'adet',
      cari_hesap: '',
      hata_cikma_zamani: '',
      description: '',
      belgeler: [],
      status: 'acik',
      resolution_notes: ''
    });
    setEditMode(false);
  };

  const openCreate = () => {
    resetForm();
    // Set current date automatically
    setForm(prev => ({
      ...prev,
      created_at: new Date().toISOString()
    }));
    setModal(true);
  };

  const openEdit = (urun) => {
    setForm({
      id: urun.id,
      stok_karti_id: urun.stok_karti_id || '',
      product_name: urun.product_name || '',
      issue_type: urun.issue_type || 'hatali',
      quantity: urun.quantity || '',
      unit: urun.unit || 'adet',
      cari_hesap: urun.cari_hesap || '',
      hata_cikma_zamani: urun.hata_cikma_zamani || '',
      description: urun.description || '',
      belgeler: urun.belgeler || [],
      status: urun.status || 'acik',
      resolution_notes: urun.resolution_notes || ''
    });
    setEditMode(true);
    setModal(true);
  };

  const openDetail = (urun) => {
    setSelectedUrun(urun);
    setDetailModal(true);
  };

  // Hata Giderildi modal açma
  const openResolveModal = (urun) => {
    setResolveUrun(urun);
    setResolveNotes('');
    setResolveModal(true);
  };

  // Hata Giderildi işlemi
  const handleResolve = async () => {
    if (!resolveNotes.trim()) {
      toast.error('Lütfen çözüm açıklaması yazın');
      return;
    }

    try {
      await axios.put(`${API}/api/depot/hatali-urunler/${resolveUrun.id}`, {
        status: 'cozuldu',
        resolution_notes: resolveNotes
      }, { headers: getAuthHeaders() });
      
      toast.success('Kayıt çözüldü olarak işaretlendi');
      setResolveModal(false);
      setResolveUrun(null);
      setResolveNotes('');
      fetchData();
    } catch (err) {
      toast.error('İşlem başarısız');
    }
  };

  const handleStokKartiChange = (value) => {
    const selected = stokKartlari.find(s => s.id === value);
    setForm(prev => ({
      ...prev,
      stok_karti_id: value,
      product_name: selected?.name || '',
      unit: selected?.unit || prev.unit
    }));
  };

  const handleBelgeUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} dosyası 5MB'dan büyük`);
        continue;
      }

      const reader = new FileReader();
      reader.onload = () => {
        setForm(prev => ({
          ...prev,
          belgeler: [...prev.belgeler, {
            baslik: '',
            dosya_adi: file.name,
            dosya: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const updateBelgeBaslik = (index, baslik) => {
    setForm(prev => ({
      ...prev,
      belgeler: prev.belgeler.map((b, i) => i === index ? { ...b, baslik } : b)
    }));
  };

  const removeBelge = (index) => {
    setForm(prev => ({
      ...prev,
      belgeler: prev.belgeler.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!form.stok_karti_id) {
      toast.error('Stok kartı seçimi zorunludur');
      return;
    }
    if (!form.quantity || form.quantity <= 0) {
      toast.error('Miktar girmeniz zorunludur');
      return;
    }
    if (!form.issue_type) {
      toast.error('Hata durumu seçimi zorunludur');
      return;
    }
    if (!form.hata_cikma_zamani) {
      toast.error('Hata çıkma zamanı seçimi zorunludur');
      return;
    }

    try {
      if (editMode) {
        await axios.put(`${API}/api/depot/hatali-urunler/${form.id}`, form, { headers: getAuthHeaders() });
        toast.success('Kayıt güncellendi');
      } else {
        await axios.post(`${API}/api/depot/hatali-urunler`, form, { headers: getAuthHeaders() });
        toast.success('Kayıt oluşturuldu ve satın almaya bildirildi');
      }
      setModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/api/depot/hatali-urunler/${id}`, { headers: getAuthHeaders() });
      toast.success('Kayıt silindi');
      fetchData();
    } catch (err) {
      toast.error('Silme başarısız');
    }
  };

  const getIssueTypeBadge = (type) => {
    switch (type) {
      case 'eksik':
        return <Badge className="bg-amber-100 text-amber-700"><AlertCircle className="h-3 w-3 mr-1" />Eksik Ürün</Badge>;
      case 'hasarli':
        return <Badge className="bg-orange-100 text-orange-700"><Package className="h-3 w-3 mr-1" />Hasarlı Ürün</Badge>;
      default:
        return <Badge variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" />Hatalı Ürün</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'cozuldu') {
      return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="h-3 w-3 mr-1" />Çözüldü</Badge>;
    }
    return <Badge className="bg-red-100 text-red-700"><Clock className="h-3 w-3 mr-1" />Açık</Badge>;
  };

  const getHataCikmaZamaniLabel = (value) => {
    const labels = {
      'teslim_alirken': 'Ürün teslim alırken',
      'paket_acilinca': 'Ürün paketi açılınca',
      'uygulama_esnasinda': 'Ürün uygulama esnasında',
      'uygulama_sonrasi': 'Uygulama sonrası'
    };
    return labels[value] || value || '-';
  };

  const birimOptions = ['adet', 'kg', 'metre', 'litre', 'paket', 'kutu', 'ton', 'metrekare', 'metre tül', 'metre küp'];

  // Toggle order expansion
  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  // Format datetime
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter by tab (grouped data)
  const filteredUrunler = urunler.filter(order => {
    if (activeTab === 'acik') return order.has_open_issues;
    if (activeTab === 'cozuldu') return order.all_resolved;
    return true;
  });

  // Count for tabs
  const openCount = urunler.filter(order => order.has_open_issues).length;
  const resolvedCount = urunler.filter(order => order.all_resolved).length;

  if (loading) {
    return (
      <div className="space-y-6" data-testid="depot-defective-loading">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Hatalı veya Eksik Ürünler</h1>
            <p className="text-gray-500">Sorunlu ürün takibi</p>
          </div>
        </div>
        <Card>
          <CardContent className="py-6">
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="depot-defective">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hatalı veya Eksik Ürünler</h1>
          <p className="text-gray-500">Sorunlu ürün takibi</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="acik" className="gap-2">
            <AlertTriangle className="h-4 w-4" />
            EKSİK/HATALI
            {openCount > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 min-w-5 p-0 flex items-center justify-center text-xs">
                {openCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="cozuldu" className="gap-2">
            <CheckCircle className="h-4 w-4" />
            ÇÖZÜLENLER
            {resolvedCount > 0 && (
              <Badge className="ml-1 h-5 min-w-5 p-0 flex items-center justify-center text-xs bg-emerald-100 text-emerald-700">
                {resolvedCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                {activeTab === 'acik' ? (
                  <><AlertTriangle className="h-5 w-5 text-red-600" /> Eksik/Hatalı ({filteredUrunler.length})</>
                ) : (
                  <><CheckCircle className="h-5 w-5 text-emerald-600" /> Çözülen Sorunlar ({filteredUrunler.length})</>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredUrunler.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  {activeTab === 'acik' ? (
                    <AlertTriangle className="h-12 w-12 mb-4 text-gray-300" />
                  ) : (
                    <CheckCircle className="h-12 w-12 mb-4 text-gray-300" />
                  )}
                  <p>Bu kategoride kayıt bulunmuyor</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Kayıt Tarihi</TableHead>
                      <TableHead>Ürün Sayısı</TableHead>
                      <TableHead>Tedarikçi</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUrunler.map((order) => (
                      <React.Fragment key={order.id}>
                        {/* Main Order Row */}
                        <TableRow 
                          data-testid={`defective-order-row-${order.id}`}
                          className={`cursor-pointer hover:bg-red-50 ${expandedOrders[order.id] ? 'bg-red-50' : ''}`}
                          onClick={() => toggleOrderExpansion(order.id)}
                        >
                          <TableCell className="w-10">
                            {order.items && order.items.length > 0 ? (
                              expandedOrders[order.id] ? (
                                <ChevronDown className="h-4 w-4 text-red-600" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-gray-400" />
                              )
                            ) : (
                              <AlertTriangle className="h-4 w-4 text-red-300" />
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              {formatDateTime(order.created_at || order.hata_cikma_zamani)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="gap-1 border-red-300 text-red-700">
                              <Layers className="h-3 w-3" />
                              {order.item_count || order.items?.length || 1} Kalem
                            </Badge>
                          </TableCell>
                          <TableCell>{order.cari_hesap || '-'}</TableCell>
                          <TableCell>
                            {order.has_open_issues ? (
                              <Badge className="bg-red-100 text-red-800">Açık Sorun Var</Badge>
                            ) : (
                              <Badge className="bg-green-100 text-green-800">Tümü Çözüldü</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedUrun(order); setDetailModal(true); }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                        
                        {/* Expanded Items Row */}
                        {expandedOrders[order.id] && order.items && order.items.length > 0 && (
                          <TableRow className="bg-red-50/50">
                            <TableCell colSpan={6} className="p-0">
                              <div className="p-4 border-l-4 border-red-400">
                                <div className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-2">
                                  <AlertTriangle className="h-4 w-4 text-red-500" />
                                  EKSİK/HATALI ÜRÜNLER ({order.items.length})
                                </div>
                                <Table>
                                  <TableHeader>
                                    <TableRow className="bg-red-100/50">
                                      <TableHead className="text-xs">Ürün Adı</TableHead>
                                      <TableHead className="text-xs">Sorun Tipi</TableHead>
                                      <TableHead className="text-xs">Beklenen</TableHead>
                                      <TableHead className="text-xs">Gelen</TableHead>
                                      <TableHead className="text-xs">Eksik</TableHead>
                                      <TableHead className="text-xs">Durum</TableHead>
                                      <TableHead className="text-xs">Açıklama</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {order.items.map((item, idx) => (
                                      <TableRow key={item.id || idx} className="hover:bg-red-100/30">
                                        <TableCell className="text-sm font-medium">{item.product_name || '-'}</TableCell>
                                        <TableCell className="text-sm">{getIssueTypeBadge(item.issue_type)}</TableCell>
                                        <TableCell className="text-sm">{item.expected_quantity || '-'} {item.unit}</TableCell>
                                        <TableCell className="text-sm">{item.received_quantity || '-'} {item.unit}</TableCell>
                                        <TableCell className="text-sm font-semibold text-red-600">
                                          {item.quantity} {item.unit}
                                        </TableCell>
                                        <TableCell className="text-sm">{getStatusBadge(item.status)}</TableCell>
                                        <TableCell className="text-sm text-gray-500 max-w-[200px] truncate">
                                          {item.description || '-'}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Modal */}
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Kayıt Düzenle' : 'Yeni Hatalı/Eksik Ürün Kaydı'}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            <div className="space-y-4">
              {/* 1. Tarih - Otomatik */}
              <div>
                <Label>Tarih</Label>
                <Input
                  value={new Date().toLocaleDateString('tr-TR')}
                  disabled
                  className="bg-gray-50"
                />
                <p className="text-xs text-gray-500 mt-1">Tarih otomatik olarak eklenir</p>
              </div>

              {/* 2. Stok Kartı Seç */}
              <div>
                <Label>Stok Kartı Seç *</Label>
                <Select value={form.stok_karti_id} onValueChange={handleStokKartiChange}>
                  <SelectTrigger data-testid="stok-karti-select">
                    <SelectValue placeholder="Stok kartı seçin..." />
                  </SelectTrigger>
                  <SelectContent>
                    {stokKartlari.map((stok) => (
                      <SelectItem key={stok.id} value={stok.id}>
                        {stok.name} ({stok.unit})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 3. Miktar */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Miktar *</Label>
                  <Input
                    type="number"
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    placeholder="0"
                    data-testid="quantity-input"
                  />
                </div>
                <div>
                  <Label>Birim</Label>
                  <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {birimOptions.map(b => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* 4. Cari Hesap */}
              <div>
                <Label>Cari Hesap</Label>
                <Input
                  value={form.cari_hesap}
                  onChange={(e) => setForm({ ...form, cari_hesap: e.target.value })}
                  placeholder="Cari hesap adı yazın..."
                  data-testid="cari-hesap-input"
                />
              </div>

              {/* 5. Hata Durumu */}
              <div>
                <Label>Hata Durumu *</Label>
                <Select value={form.issue_type} onValueChange={(v) => setForm({ ...form, issue_type: v })}>
                  <SelectTrigger data-testid="issue-type-select">
                    <SelectValue placeholder="Hata durumu seçin..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="eksik">Eksik Ürün</SelectItem>
                    <SelectItem value="hatali">Hatalı Ürün</SelectItem>
                    <SelectItem value="hasarli">Hasarlı Ürün</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* 6. Hata Çıkma Zamanı */}
              <div>
                <Label>Hata Çıkma Zamanı *</Label>
                <Select value={form.hata_cikma_zamani} onValueChange={(v) => setForm({ ...form, hata_cikma_zamani: v })}>
                  <SelectTrigger data-testid="hata-zamani-select">
                    <SelectValue placeholder="Hata ne zaman tespit edildi?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="teslim_alirken">Ürün teslim alırken</SelectItem>
                    <SelectItem value="paket_acilinca">Ürün paketi açılınca</SelectItem>
                    <SelectItem value="uygulama_esnasinda">Ürün uygulama esnasında</SelectItem>
                    <SelectItem value="uygulama_sonrasi">Uygulama sonrası</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* 7. Açıklama */}
              <div>
                <Label>Açıklama</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Sorunun detaylı açıklaması..."
                  rows={3}
                  data-testid="description-textarea"
                />
              </div>

              {/* 8. Belge Yükleme */}
              <div>
                <Label>Belgeler</Label>
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 mt-2">
                  <input
                    type="file"
                    id="belge-upload"
                    multiple
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={handleBelgeUpload}
                    className="hidden"
                  />
                  <label
                    htmlFor="belge-upload"
                    className="flex flex-col items-center justify-center cursor-pointer py-4"
                  >
                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-500">Belge yüklemek için tıklayın</span>
                    <span className="text-xs text-gray-400 mt-1">Birden fazla dosya seçebilirsiniz (Max: 5MB)</span>
                  </label>
                </div>

                {/* Yüklenen Belgeler */}
                {form.belgeler.length > 0 && (
                  <div className="mt-4 space-y-3">
                    <Label className="text-sm font-medium">Yüklenen Belgeler ({form.belgeler.length})</Label>
                    {form.belgeler.map((belge, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <FileText className="h-5 w-5 text-blue-500 mt-1 flex-shrink-0" />
                        <div className="flex-1 space-y-2">
                          <Input
                            value={belge.baslik}
                            onChange={(e) => updateBelgeBaslik(index, e.target.value)}
                            placeholder="Belge başlığı yazın..."
                            className="text-sm"
                            data-testid={`belge-baslik-${index}`}
                          />
                          <p className="text-xs text-gray-500">{belge.dosya_adi}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeBelge(index)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setModal(false)}>İptal</Button>
            <Button onClick={handleSubmit} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="submit-btn">
              <ShoppingCart className="h-4 w-4" />
              {editMode ? 'Güncelle' : 'Kaydet ve Satın Almaya Bildir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hata Giderildi Modal */}
      <Dialog open={resolveModal} onOpenChange={setResolveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
              Hata Giderildi
            </DialogTitle>
          </DialogHeader>
          {resolveUrun && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Ürün:</span>
                    <span className="ml-2 font-medium">{resolveUrun.product_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Miktar:</span>
                    <span className="ml-2 font-medium">{resolveUrun.quantity} {resolveUrun.unit}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Hata Durumu:</span>
                    <span className="ml-2">{getIssueTypeBadge(resolveUrun.issue_type)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Tarih:</span>
                    <span className="ml-2 font-medium">{new Date(resolveUrun.created_at).toLocaleDateString('tr-TR')}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <Label>Çözüm Açıklaması *</Label>
                <Textarea
                  value={resolveNotes}
                  onChange={(e) => setResolveNotes(e.target.value)}
                  placeholder="Hatanın nasıl giderildiğini açıklayın..."
                  rows={4}
                  className="mt-2"
                  data-testid="resolve-notes-textarea"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveModal(false)}>İptal</Button>
            <Button onClick={handleResolve} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="resolve-confirm-btn">
              <CheckCircle className="h-4 w-4" />
              Tamam
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={detailModal} onOpenChange={setDetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Kayıt Detayı</DialogTitle>
          </DialogHeader>
          {selectedUrun && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Tarih</Label>
                  <p className="font-medium">{new Date(selectedUrun.created_at).toLocaleDateString('tr-TR')}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Durum</Label>
                  <p>{getStatusBadge(selectedUrun.status)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Ürün</Label>
                  <p className="font-medium">{selectedUrun.product_name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Hata Durumu</Label>
                  <p>{getIssueTypeBadge(selectedUrun.issue_type)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Miktar</Label>
                  <p className="font-medium">{selectedUrun.quantity} {selectedUrun.unit}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Cari Hesap</Label>
                  <p className="font-medium">{selectedUrun.cari_hesap || '-'}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-500">Hata Çıkma Zamanı</Label>
                  <p className="font-medium">{getHataCikmaZamaniLabel(selectedUrun.hata_cikma_zamani)}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-500">Açıklama</Label>
                  <p className="font-medium">{selectedUrun.description || '-'}</p>
                </div>
                {selectedUrun.resolution_notes && (
                  <div className="col-span-2">
                    <Label className="text-gray-500">Çözüm Notları</Label>
                    <p className="font-medium">{selectedUrun.resolution_notes}</p>
                  </div>
                )}
                <div className="col-span-2">
                  <Label className="text-gray-500">Oluşturan</Label>
                  <p className="font-medium">{selectedUrun.created_by_name || '-'}</p>
                </div>
              </div>

              {/* Belgeler */}
              {selectedUrun.belgeler && selectedUrun.belgeler.length > 0 && (
                <div>
                  <Label className="text-gray-500 mb-2 block">Belgeler ({selectedUrun.belgeler.length})</Label>
                  <div className="space-y-2">
                    {selectedUrun.belgeler.map((belge, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-blue-500" />
                          <div>
                            <p className="font-medium text-sm">{belge.baslik || `Belge ${index + 1}`}</p>
                            <p className="text-xs text-gray-500">{belge.dosya_adi}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {belge.dosya && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(belge.dosya, '_blank')}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Görüntüle
                              </Button>
                              <a href={belge.dosya} download={belge.dosya_adi}>
                                <Button variant="outline" size="sm">
                                  <Download className="h-4 w-4 mr-1" />
                                  İndir
                                </Button>
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailModal(false)}>Kapat</Button>
            {selectedUrun && selectedUrun.status === 'acik' && (
              <>
                <Button onClick={() => { setDetailModal(false); openResolveModal(selectedUrun); }} className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Hata Giderildi
                </Button>
                <Button onClick={() => { setDetailModal(false); openEdit(selectedUrun); }} className="bg-blue-600 hover:bg-blue-700">
                  <Pencil className="h-4 w-4 mr-1" />
                  Düzenle
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DepotDefective;
